import jsPDF from 'jspdf';
import 'jspdf-autotable';

/**
 * Generates and downloads a detailed PDF report for a single case.
 * @param {Object} row - The full case details object
 * @param {string} companyName - Name of the law firm/company
 * @param {string} companyLogo - Base64 string or image URL
 */
const DownloadCaseReportPdf = (row, companyName, companyLogo) => {
  return new Promise((resolve, reject) => {
    try {
      if (!row) {
        throw new Error("No case data provided");
      }

      // 1. Initialize Document (Portrait mode, A4 size)
      const doc = new jsPDF('p', 'mm', 'a4');
      const pageWidth = doc.internal.pageSize.getWidth();
      let currentY = 15;

      // 2. Render Header (Company Logo & Name)
      if (companyLogo) {
        try {
          doc.addImage(companyLogo, 'PNG', 14, currentY, 15, 15);
          doc.setFontSize(18);
          doc.setFont('helvetica', 'bold');
          doc.text(companyName || "Legal Management System", 34, currentY + 10);
          currentY += 22;
        } catch (e) {
          doc.setFontSize(18);
          doc.setFont('helvetica', 'bold');
          doc.text(companyName || "Legal Management System", 14, currentY + 5);
          currentY += 15;
        }
      } else {
        doc.setFontSize(18);
        doc.setFont('helvetica', 'bold');
        doc.text(companyName || "Legal Management System", 14, currentY + 5);
        currentY += 15;
      }

      // 3. Centered Title & Current Date
      doc.setFontSize(14);
      doc.setFont('helvetica', 'bold');
      doc.text("CASE DETAILS REPORT", pageWidth / 2, currentY, { align: 'center' });
      
      doc.setFontSize(9);
      doc.setFont('helvetica', 'normal');
      const todayDate = new Date().toLocaleDateString('en-GB'); // DD/MM/YYYY
      doc.text(`Generated On: ${todayDate}`, pageWidth - 14, currentY + 1, { align: 'right' });
      
      currentY += 5;
      doc.setDrawColor(200, 200, 200);
      doc.line(14, currentY, pageWidth - 14, currentY); // Horizontal separator line
      currentY += 8;

      // 4. Primary Case Details Block (AutoTable Key-Value Layout)
      const formatDate = (dateStr) => {
        if (!dateStr) return 'N/A';
        return dateStr.split('T')[0]; // Simple extraction of YYYY-MM-DD
      };

    //   const primaryDetailsHeaders = [["Field", "Details", "Field", "Details"]];
      const primaryDetailsRows = [
        [
          { content: "Case No:", styles: { fontStyle: 'bold' } }, row.case_no || 'N/A',
          { content: "Case Type:", styles: { fontStyle: 'bold' } }, row.case_type_name || 'N/A'
        ],
        [
          { content: "Case Name:", styles: { fontStyle: 'bold' } }, row.case_name || 'N/A',
          { content: "Filing Date:", styles: { fontStyle: 'bold' } }, formatDate(row.filing_date)
        ],
        [
          // { content: "Default Judge:", styles: { fontStyle: 'bold' } }, row.judge_name || 'N/A',
          { content: "Court Name:", styles: { fontStyle: 'bold' } }, row.court_name || 'N/A',
          { content: "Opposite Party:", styles: { fontStyle: 'bold' } }, row.opposite_party || 'N/A'
        ],
        [
          { content: "Stage Name:", styles: { fontStyle: 'bold' } }, row.stage_name || 'N/A',
          { content: "Current Status:", styles: { fontStyle: 'bold' } }, row.status || 'N/A',
          // { content: "Priority Status:", styles: { fontStyle: 'bold' } }, row.priority || 'N/A'
        ],
        // [
        //   // { content: "Current Status:", styles: { fontStyle: 'bold' } }, row.status || 'N/A',
        //   // { content: "Court Name:", styles: { fontStyle: 'bold' } }, row.court_name || 'N/A'
        // ]
      ];

      doc.autoTable({
        startY: currentY,
        // head: primaryDetailsHeaders,
        body: primaryDetailsRows,
        theme: 'plain',
        headStyles: { fillOpacity: 0, textColor: [0, 0, 0], fontSize: 0, cellPadding: 0 }, // Hide header row space visually
        bodyStyles: { fontSize: 10, cellPadding: 3, valign: 'middle' },
        columnStyles: {
          0: { cellWidth: 30 },
          1: { cellWidth: 60 },
          2: { cellWidth: 35 },
          3: { cellWidth: 60 }
        },
        styles: { overflow: 'linebreak' }
      });

      currentY = doc.lastAutoTable.finalY + 8;

      // 5. Clients & Associated Emails Block
      doc.setFontSize(12);
      doc.setFont('helvetica', 'bold');
      doc.text("Client Information", 14, currentY);
      currentY += 4;

      const clientHeaders = ["Client Name", "Associated Email"];
      const clientRows = [];
      const totalClients = Math.max(row.client_names?.length || 0, row.client_emails?.length || 0);
      
      for (let i = 0; i < totalClients; i++) {
        clientRows.push([
          row.client_names?.[i] || 'N/A',
          row.client_emails?.[i] || 'N/A'
        ]);
      }

      doc.autoTable({
        startY: currentY,
        head: [clientHeaders],
        body: clientRows,
        theme: 'grid',
        headStyles: { fillColor: [74, 85, 104], fontSize: 10 }, // Neutral slate grey
        bodyStyles: { fontSize: 9, cellPadding: 3 }
      });

      currentY = doc.lastAutoTable.finalY + 8;

      // 6. Case History / Hearings Timeline Block
      doc.setFontSize(12);
      doc.setFont('helvetica', 'bold');
      doc.text("Hearing Logs & Timeline History", 14, currentY);
      currentY += 4;

      if (!row.hearings || row.hearings.length === 0) {
        doc.setFontSize(10);
        doc.setFont('helvetica', 'italic');
        doc.text("No hearing records tracking found for this case file.", 14, currentY);
      } else {
        const hearingHeaders = ["Date", "Next Date", "Room", "Judge", "Status", "Summary"];
        
        const hearingRows = row.hearings.map((h) => [
          formatDate(h.hearing_date),
          formatDate(h.next_hearing_date),
          h.court_room || '-',
          h.judge_name || '-',
          h.hearing_status || '-',
          // `Summary: ${h.summary || 'N/A'}\nOrder: ${h.order_details || 'N/A'}`
        ]);

        doc.autoTable({
          startY: currentY,
          head: [hearingHeaders],
          body: hearingRows,
          theme: 'striped',
          headStyles: { fillColor: [25, 118, 210], fontSize: 10, halign: 'center' }, // MUI Main Blue
          bodyStyles: { fontSize: 9, valign: 'top' },
          columnStyles: {
            0: { cellWidth: 24 }, // Hearing Date
            1: { cellWidth: 24 }, // Next Hearing Date
            2: { cellWidth: 20 }, // Court Room
            3: { cellWidth: 25 }, // Judge
            4: { cellWidth: 22 }, // Status
            // 5: { cellWidth: 70 }  // Combined Summary & Order
          },
          styles: { overflow: 'linebreak' }
        });
      }

      // 7. Trigger System Download
      const fileName = `Case_${row.case_no || 'Report'}_Details.pdf`;
      doc.save(fileName);
      
      resolve(); // Promise resolution to trigger toast success
    } catch (error) {
      reject(error);
    }
  });
};

export default DownloadCaseReportPdf;